import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Typography } from "@mui/material";
import { useState } from "react";
import AppSnackbar from "../components/Common/Snackbar/AppSnackbar";
import ScreenLoader from "../components/Common/ScreenLoader/ScreenLoader";
import NotificationForm from "../components/inAppNotificationManagement/NotificationForm";
import NotificationPageLayout from "../components/inAppNotificationManagement/NotificationPageLayout";
import { useNotificationDetail } from "../components/inAppNotificationManagement/useNotificationDetail";
import { withNotificationPageAuth } from "../components/inAppNotificationManagement/notificationPageAuth";
import customConfig from "../config";
import {
  buildNotificationListRouteState,
  getInAppNotificationPermission,
  IN_APP_NOTIFICATION_ROUTES,
} from "../constants/inAppNotificationConstant";

const EditNotificationPage = ({ userRoles = [], userLevel = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notificationId } = useParams();
  const { notification, loading, loadError } = useNotificationDetail(notificationId, {
    fallbackTargetUserCount: location.state?.targetUserCount,
  });
  const [apiFeedbackMsg, setApiFeedbackMsg] = useState({
    open: false,
    type: "info",
    msg: "",
  });

  const canEdit = getInAppNotificationPermission(
    userRoles,
    "editNotification",
    userLevel,
  );

  const navigateBack = (refresh = false, feedback) => {
    const resolvedTargetType = notification?.targetType || "broadcast";
    navigate(IN_APP_NOTIFICATION_ROUTES.list, {
      state: buildNotificationListRouteState({
        targetType: resolvedTargetType,
        refresh,
        feedback,
      }),
    });
  };

  if (!canEdit) {
    return (
      <NotificationPageLayout
        breadcrumbLabel="Edit Notification"
        title="Edit Notification"
        subtitle="Update notification content, delivery settings, and card or carousel configuration."
      >
        <Typography color="text.secondary">
          You do not have permission to edit notifications.
        </Typography>
      </NotificationPageLayout>
    );
  }

  if (loading) {
    return (
      <NotificationPageLayout
        breadcrumbLabel="Edit Notification"
        title="Edit Notification"
        subtitle="Update notification content, delivery settings, and card or carousel configuration."
      >
        <ScreenLoader message={customConfig.FETCHING_DATA} />
      </NotificationPageLayout>
    );
  }

  if (loadError || !notification) {
    return (
      <NotificationPageLayout
        breadcrumbLabel="Edit Notification"
        title="Edit Notification"
        subtitle="Update notification content, delivery settings, and card or carousel configuration."
      >
        <Typography color="error">{loadError || customConfig.FETCH_FAILURE}</Typography>
      </NotificationPageLayout>
    );
  }

  return (
    <NotificationPageLayout
      breadcrumbLabel="Edit Notification"
      title="Edit Notification"
      subtitle="Update notification content, delivery settings, and card or carousel configuration."
    >
      <NotificationForm
        mode="edit"
        initialData={notification}
        defaultTargetType={notification.targetType}
        fallbackTargetUserCount={location.state?.targetUserCount}
        userRoles={userRoles}
        userLevel={userLevel}
        onCancel={() => navigateBack()}
        onSuccess={({ message }) =>
          navigateBack(true, message ? { type: "success", msg: message } : undefined)
        }
        feedbackHandler={(feedback) =>
          setApiFeedbackMsg({ ...feedback, open: true })
        }
      />

      <AppSnackbar
        open={apiFeedbackMsg.open}
        type={apiFeedbackMsg.type}
        message={apiFeedbackMsg.msg}
        onClose={() => setApiFeedbackMsg((prev) => ({ ...prev, open: false }))}
      />
    </NotificationPageLayout>
  );
};

export default withNotificationPageAuth(EditNotificationPage);
